import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Phone, Mail } from "lucide-react";

// Contact Section (used on homepage)
const ContactSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section 
      ref={ref}
      data-testid="contact-section"
      className="py-12 md:py-16 px-4 md:px-8 lg:px-12 bg-[#FAFAFA]"
    >
      <div className="max-w-4xl mx-auto">
        {/* Contact Us Header and Address - Centered */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 
            data-testid="contact-header"
            className="text-3xl md:text-4xl font-bold text-[#1A1A1A] mb-6"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Contact Us
          </h2>
          <div className="text-[#666666] text-base md:text-lg leading-relaxed">
            <p className="font-semibold text-[#1A1A1A]">Murphy's Giving Market</p>
            <p>7408 West Chester Pike</p>
            <p>Upper Darby, PA 19082</p>
          </div>
        </motion.div>

        {/* Contact Info - Two rows */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="flex flex-col items-center gap-4"
        >
          {/* Row 1: Phone numbers and Note */}
          <div className="flex items-start gap-2 max-w-md">
            <Phone size={20} className="text-[#1877F2] mt-0.5 flex-shrink-0" />
            <div>
              <div className="mb-1">
                <span className="text-[#666666]">Office: </span>
                <a 
                  href="tel:610-271-8105"
                  data-testid="contact-phone-office"
                  className="text-[#666666] hover:text-[#1877F2] transition-colors"
                >
                  610-271-8105
                </a>
              </div>
              <div className="mb-1">
                <span className="text-[#666666]">Cell: </span>
                <a 
                  href="tel:267-322-1088"
                  data-testid="contact-phone-cell"
                  className="text-[#666666] hover:text-[#1877F2] transition-colors"
                >
                  267-322-1088
                </a>
              </div>
              <p className="text-sm text-[#666666] mt-1">
                Please call between 9:00am and 4:00pm. If you leave a message, someone will call you back within 48 hours.
              </p>
            </div>
          </div>

          {/* Row 2: Email and Facebook */}
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-8">
            <a 
              href="mailto:murphysgivingmarket@gmail.com"
              data-testid="contact-email"
              className="flex items-center gap-2 text-[#666666] hover:text-[#1877F2] transition-colors"
            >
              <Mail size={20} className="text-[#1877F2]" />
              <span>murphysgivingmarket@gmail.com</span>
            </a>

            <a 
              href="https://www.facebook.com/murphysgivingmarket"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-facebook"
              className="flex items-center gap-2 text-[#666666] hover:text-[#1877F2] transition-colors"
            >
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                width="20" 
                height="20" 
                viewBox="0 0 24 24" 
                fill="currentColor"
                className="text-[#1877F2]"
              >
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
              <span>Facebook</span>
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ContactSection;
